import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from 'antd';
import { message } from '../utils/antdMessage';
import apiFetch from '../utils/apiFetch';
import { publishOrthoStatus } from '../utils/orthoStatusBus';
import { isValidWgs84Gps, readFileExifGps } from '../utils/exifGps';
import { postObjectToOss } from '../utils/orthoToolbox';
import orthoReadJson from './orthoReadJson';

/**
 * 航拍任务列表：CRUD、上传、pending/layers 加载与轮询
 */
export default function useOrthoTaskList({
  canManage = false,
  canApprove = false,
  canViewLayers = false,
  holdPanelOpenRef,
  setExpanded,
  setPinned
}) {
  const [tasks, setTasks] = useState([]);
  const [pending, setPending] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [title, setTitle] = useState('');
  const [code, setCode] = useState('');
  const [wantGlb, setWantGlb] = useState(false);
  /** 建模成功后清理 OSS inbox 中转（只留成果）；调试期默认关 */
  const [purgeInboxOnSuccess, setPurgeInboxOnSuccess] = useState(false);
  const [busy, setBusy] = useState(false);
  const [layers, setLayers] = useState([]);
  const [revealedCode, setRevealedCode] = useState('');
  const [orthoUploadText, setOrthoUploadText] = useState('');
  const orthoPhotoRef = useRef(null);
  const orthoFolderRef = useRef(null);
  const [assetsModalOpen, setAssetsModalOpen] = useState(false);
  const [orthoManageOpen, setOrthoManageOpen] = useState(false);

  const activeTask = tasks.find((t) => t.id === activeId) || null;

  useEffect(() => {
    if (!activeTask) return;
    if (activeTask.wantGlb != null) setWantGlb(!!activeTask.wantGlb);
    if (activeTask.purgeInboxOnSuccess != null) {
      setPurgeInboxOnSuccess(!!activeTask.purgeInboxOnSuccess);
    }
  }, [activeTask?.id, activeTask?.wantGlb, activeTask?.purgeInboxOnSuccess]);

  const refreshStatusBar = useCallback(async () => {
    try {
      const res = await apiFetch('/api/ortho/status-summary');
      const data = await res.json().catch(() => ({}));
      if (data?.success) {
        publishOrthoStatus({
          active: !!data.active,
          text: data.text || '',
          primary: data.primary || null
        });
      }
    } catch (_) {
      publishOrthoStatus({ active: false, text: '' });
    }
  }, []);

  const loadTasks = useCallback(async () => {
    if (!canManage) return;
    try {
      const data = await orthoReadJson(await apiFetch('/api/ortho/tasks'));
      setTasks(data.tasks || []);
      // 避免把 activeId 放进依赖，防止展开时反复重建回调引发闪烁
      setActiveId((prev) => prev || data.tasks?.[0]?.id || null);
    } catch (e) {
      console.warn('[ortho] 任务列表', e);
    }
  }, [canManage]);

  const loadPending = useCallback(async () => {
    if (!canApprove) return;
    try {
      const data = await orthoReadJson(await apiFetch('/api/ortho/tasks/pending-approvals'));
      setPending(data.tasks || []);
    } catch (_) { /* ignore */ }
  }, [canApprove]);

  const loadLayers = useCallback(async () => {
    if (!canViewLayers && !canManage) return;
    try {
      const data = await orthoReadJson(await apiFetch('/api/ortho/layers'));
      const list = Array.isArray(data.layers) ? data.layers : [];
      setLayers(list);
      if (list.length === 0) {
        console.warn('[ortho] /api/ortho/layers 返回空列表');
      }
      return list;
    } catch (e) {
      console.warn('[ortho] 图层列表加载失败', e);
      message.warning(e?.message || '航拍图层列表加载失败，请刷新后重试');
      return [];
    }
  }, [canViewLayers, canManage]);

  /** 航拍管理弹窗打开时加快任务刷新 */
  useEffect(() => {
    if (!orthoManageOpen || !canManage) return undefined;
    loadTasks();
    const t = setInterval(loadTasks, 5000);
    return () => clearInterval(t);
  }, [orthoManageOpen, canManage, loadTasks]);

  useEffect(() => {
    refreshStatusBar();
    const t = setInterval(refreshStatusBar, 20000);
    return () => clearInterval(t);
  }, [refreshStatusBar]);

  const createTask = async () => {
    setBusy(true);
    try {
      const data = await orthoReadJson(await apiFetch('/api/ortho/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: title || '航拍任务' })
      }));
      message.success(`已创建任务 #${data.task.id}`);
      setTitle('');
      setActiveId(data.task.id);
      await loadTasks();
      refreshStatusBar();
    } catch (e) {
      message.error(e.message || '创建失败');
    } finally {
      setBusy(false);
    }
  };

  const isOrthoImageFile = (file) => {
    if (!file?.name) return false;
    if (file.type && file.type.startsWith('image/')) return true;
    return /\.(jpe?g|png|tif|tiff|webp|heic|bmp)$/i.test(file.name);
  };

  /** 多选文件或整个文件夹；文件夹内只取图片，并用相对路径避免重名覆盖 */
  const uploadOrthoPhotos = async (files, { fromFolder = false } = {}) => {
    const task = activeTask;
    if (!task?.id || !files?.length) return;
    if (!['draft', 'ready'].includes(task.status)) {
      message.warning('当前状态不可上传素材，请先「撤回就绪」或等任务回到草稿');
      return;
    }
    const list = Array.from(files).filter(isOrthoImageFile);
    const skipped = files.length - list.length;
    if (!list.length) {
      message.warning(fromFolder ? '所选文件夹内没有可识别的图片' : '请选择图片文件');
      return;
    }
    if (list.length > 500) {
      Modal.warning({
        title: '照片数量较多',
        content: `本次将上传 ${list.length} 张（系统默认上限约 500）。建议先分批，或确认后端 ORTHO_MAX_PHOTOS 已调大。`
      });
    }

    setBusy(true);
    setPinned(true);
    setExpanded(true);
    holdPanelOpenRef.current = true;
    let ok = 0;
    let fail = 0;
    try {
      if (skipped > 0) {
        setOrthoUploadText(`已忽略非图片 ${skipped} 个，准备上传 ${list.length} 张…`);
      }
      for (let i = 0; i < list.length; i += 1) {
        const file = list[i];
        // 文件夹上传：用相对路径区分子目录同名文件
        const rel = String(file.webkitRelativePath || file.name).replace(/\\/g, '/');
        const safeName = rel.replace(/[^a-zA-Z0-9._\-\/\u4e00-\u9fa5]/g, '_');
        setOrthoUploadText(`上传中 ${i + 1}/${list.length}：${rel}`);
        try {
          const credData = await orthoReadJson(await apiFetch('/api/ortho/upload/sts', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ taskId: task.id, fileName: safeName })
          }));
          const upload = credData.upload || credData;
          if (upload?.mode === 'disabled') {
            throw new Error(upload.message || 'OSS 未配置');
          }
          const objectKey = upload.objectKey || upload.key;
          if (!objectKey) throw new Error('未返回 objectKey');
          await postObjectToOss(upload, file, objectKey, ({ percent }) => {
            setOrthoUploadText(`上传中 ${i + 1}/${list.length}：${file.name} ${percent}%`);
          });
          let lon;
          let lat;
          try {
            const gps = await readFileExifGps(file);
            if (gps) {
              lon = gps.lon;
              lat = gps.lat;
            }
          } catch (_) { /* ignore */ }
          await orthoReadJson(await apiFetch('/api/ortho/upload/register', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              taskId: task.id,
              ossKey: objectKey,
              bytes: file.size,
              ...(isValidWgs84Gps(lon, lat) ? { lon, lat } : {})
            })
          }));
          ok += 1;
        } catch (oneErr) {
          fail += 1;
          console.warn('[ortho] 单张失败', file.name, oneErr);
          // 大批量时单张失败不中断整批
          if (!fromFolder) throw oneErr;
        }
        // 每 10 张刷新一次素材计数
        if ((i + 1) % 10 === 0 || i === list.length - 1) {
          await loadTasks();
        }
      }
      const tip = fail
        ? `已登记 ${ok} 张，失败 ${fail} 张${skipped ? `，忽略非图片 ${skipped}` : ''}`
        : (task.status === 'ready'
          ? `已登记 ${ok} 张（已就绪），请点「密度预览/框选渲染区」`
          : `已登记 ${ok} 张，可继续上传或点「素材就绪」`);
      setOrthoUploadText(tip);
      message.success(tip);
      await loadTasks();
      refreshStatusBar();
    } catch (e) {
      setOrthoUploadText(e.message || '上传失败');
      message.error(e.message || '上传失败');
      await loadTasks();
    } finally {
      setBusy(false);
      holdPanelOpenRef.current = false;
      if (orthoPhotoRef.current) orthoPhotoRef.current.value = '';
      if (orthoFolderRef.current) orthoFolderRef.current.value = '';
    }
  };

  const deleteTask = async (id) => {
    const taskId = id || activeId;
    if (!taskId) return;
    setBusy(true);
    try {
      const data = await orthoReadJson(await apiFetch(`/api/ortho/tasks/${taskId}`, { method: 'DELETE' }));
      message.success(data.message || '已删除');
      if (activeId === taskId) setActiveId(null);
      await loadTasks();
      await loadPending();
      refreshStatusBar();
    } catch (e) {
      message.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return {
    tasks,
    setTasks,
    pending,
    setPending,
    activeId,
    setActiveId,
    activeTask,
    title,
    setTitle,
    code,
    setCode,
    wantGlb,
    setWantGlb,
    purgeInboxOnSuccess,
    setPurgeInboxOnSuccess,
    busy,
    setBusy,
    layers,
    setLayers,
    revealedCode,
    setRevealedCode,
    orthoUploadText,
    setOrthoUploadText,
    orthoPhotoRef,
    orthoFolderRef,
    assetsModalOpen,
    setAssetsModalOpen,
    orthoManageOpen,
    setOrthoManageOpen,
    refreshStatusBar,
    loadTasks,
    loadPending,
    loadLayers,
    createTask,
    uploadOrthoPhotos,
    deleteTask
  };
}
